/**
 * Validated Task Form
 * 
 * Production-ready form for creating and editing workflow tasks with comprehensive validation
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    JsonInput,
    MultiSelect,
    NumberInput,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconAlertTriangle,
    IconInfoCircle,
    IconX
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { useTaskMutations } from '../data/hooks/useTask';

interface TaskData {
    instanceId: string;
    name: string;
    description?: string;
    taskType: 'user' | 'system' | 'approval' | 'review' | 'escalation';
    priority: 'low' | 'normal' | 'high' | 'urgent';
    assignedTo?: string;
    assignedTeam?: string;
    dueDate?: string;
    estimatedDuration?: number;
    dependencies?: string[];
    context?: Record<string, any>;
    formSchema?: Record<string, any>;
    approvalRequired?: boolean;
}

interface ValidatedTaskFormProps {
    initialData?: Partial<TaskData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: TaskData) => void;
    onCancel?: () => void;
}

export const ValidatedTaskForm: React.FC<ValidatedTaskFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createTask, updateTask } = useTaskMutations();
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Initialize validated form
    const form = useValidatedForm<Partial<TaskData>>({
        schemaName: 'Task' as any, // Add Task schema to validation
        initialValues: {
            instanceId: initialData?.instanceId || '',
            name: initialData?.name || '',
            description: initialData?.description || '',
            taskType: initialData?.taskType || 'user',
            priority: initialData?.priority || 'normal',
            assignedTo: initialData?.assignedTo || '',
            assignedTeam: initialData?.assignedTeam || '',
            dueDate: initialData?.dueDate || undefined,
            estimatedDuration: initialData?.estimatedDuration || undefined,
            dependencies: initialData?.dependencies || [],
            context: initialData?.context || {},
            formSchema: initialData?.formSchema || {},
            approvalRequired: initialData?.approvalRequired || false
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Task' as any,
        () => ({
            mutate: async (data: Partial<TaskData>) => {
                if (mode === 'create') {
                    return await createTask(data as any);
                } else {
                    return await updateTask(initialData?.instanceId || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Task type options
    const taskTypeOptions = [
        { value: 'user', label: 'User Task - Requires human interaction' },
        { value: 'system', label: 'System Task - Automated processing' },
        { value: 'approval', label: 'Approval Task - Requires authorization' },
        { value: 'review', label: 'Review Task - Content or decision review' },
        { value: 'escalation', label: 'Escalation Task - Escalated from another task' }
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority' },
        { value: 'normal', label: 'Normal Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'urgent', label: 'Urgent Priority' }
    ];

    // Duration presets (in minutes)
    const durationPresets = [
        { value: 15, label: '15 minutes' },
        { value: 30, label: '30 minutes' },
        { value: 60, label: '1 hour' },
        { value: 120, label: '2 hours' },
        { value: 240, label: '4 hours' },
        { value: 480, label: '1 day' },
        { value: 1440, label: '3 days' },
        { value: 2880, label: '1 week' }
    ];

    // Handle form submission
    const handleSubmit = async (values: Partial<TaskData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<TaskData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Title order={3}>
                                {mode === 'create' ? 'Create' : 'Edit'} Task
                            </Title>
                            <Text size="sm" c="dimmed">
                                Task data is validated using JSON Schema
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Basic Task Information */}
                <Card withBorder>
                    <Title order={4} mb="md">Task Details</Title>
                    <Stack gap="md">
                        <TextInput
                            label="Instance ID"
                            placeholder="workflow-instance-123"
                            required
                            {...form.getInputProps('instanceId')}
                            error={form.getFieldError('instanceId')}
                            rightSection={
                                form.isFieldValid('instanceId') && form.values.instanceId ? 
                                <IconCheck size={16} color="green" /> : undefined
                            }
                        />

                        <TextInput
                            label="Task Name"
                            placeholder="Review customer application"
                            required
                            {...form.getInputProps('name')}
                            error={form.getFieldError('name')}
                        />

                        <Textarea
                            label="Description"
                            placeholder="Detailed description of what needs to be done..."
                            minRows={3}
                            {...form.getInputProps('description')}
                            error={form.getFieldError('description')}
                        />

                        <Group grow>
                            <Select
                                label="Task Type"
                                data={taskTypeOptions}
                                required
                                {...form.getInputProps('taskType')}
                                error={form.getFieldError('taskType')}
                            />

                            <Select
                                label="Priority"
                                data={priorityOptions}
                                {...form.getInputProps('priority')}
                                error={form.getFieldError('priority')}
                            />
                        </Group>
                    </Stack>
                </Card>

                {/* Assignment */}
                <Card withBorder>
                    <Title order={4} mb="md">Assignment</Title>
                    <Stack gap="md">
                        <Group grow>
                            <TextInput
                                label="Assigned To (User ID)"
                                placeholder="user123"
                                {...form.getInputProps('assignedTo')}
                                error={form.getFieldError('assignedTo')}
                            />

                            <TextInput
                                label="Assigned Team"
                                placeholder="approval-team"
                                {...form.getInputProps('assignedTeam')}
                                error={form.getFieldError('assignedTeam')}
                            />
                        </Group>

                        <Group grow>
                            <DateTimePicker
                                label="Due Date"
                                placeholder="Select due date and time"
                                value={form.values.dueDate ? new Date(form.values.dueDate) : null}
                                onChange={(date) => form.setFieldValue('dueDate', date ? String(date) : null)}
                                error={form.getFieldError('dueDate')}
                            />

                            <Select
                                label="Estimated Duration"
                                placeholder="Select duration"
                                data={durationPresets.map(preset => ({
                                    value: preset.value.toString(),
                                    label: preset.label
                                }))}
                                value={form.values.estimatedDuration?.toString()}
                                onChange={(value: string | null) => form.setFieldValue('estimatedDuration', value ? parseInt(value) : undefined)}
                                error={form.getFieldError('estimatedDuration')}
                            />
                        </Group>
                    </Stack>
                </Card>

                {/* Advanced Options */}
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Advanced Options</Title>
                        <Button
                            variant="light"
                            size="xs"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                        >
                            {showAdvanced ? 'Hide' : 'Show'} Advanced
                        </Button>
                    </Group>

                    {showAdvanced && (
                        <Stack gap="md">
                            <MultiSelect
                                label="Dependencies (Task IDs)"
                                placeholder="Enter task IDs this task depends on"
                                data={[]}
                                searchable
                                value={form.values.dependencies || []}
                                onChange={(values: string[]) => form.setFieldValue('dependencies', values)}
                                error={form.getFieldError('dependencies')}
                            />

                            <Checkbox
                                label="Approval Required"
                                description="This task requires approval before completion"
                                {...form.getInputProps('approvalRequired', { type: 'checkbox' })}
                            />

                            <JsonInput
                                label="Task Context (JSON)"
                                placeholder='{ "key": "value" }'
                                validationError="Invalid JSON"
                                formatOnBlur
                                autosize
                                minRows={4}
                                maxRows={10}
                                value={typeof form.values.context === 'string' ? form.values.context : JSON.stringify(form.values.context || {}, null, 2)}
                                onChange={(value) => {
                                    try {
                                        const parsed = JSON.parse(value || '{}');
                                        form.setFieldValue('context', parsed);
                                    } catch {
                                        form.setFieldValue('context', value);
                                    }
                                }}
                                error={form.getFieldError('context')}
                            />

                            <JsonInput
                                label="Form Schema (JSON)"
                                placeholder='{ "type": "object", "properties": {...} }'
                                validationError="Invalid JSON Schema"
                                formatOnBlur
                                autosize
                                minRows={4}
                                maxRows={10}
                                value={typeof form.values.formSchema === 'string' ? form.values.formSchema : JSON.stringify(form.values.formSchema || {}, null, 2)}
                                onChange={(value) => {
                                    try {
                                        const parsed = JSON.parse(value || '{}');
                                        form.setFieldValue('formSchema', parsed);
                                    } catch {
                                        form.setFieldValue('formSchema', value);
                                    }
                                }}
                                error={form.getFieldError('formSchema')}
                            />
                        </Stack>
                    )}
                </Card>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Task will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Task' : 'Update Task'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Task saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedTaskForm;